import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { permissionApi, roleApi } from '@/services/permissionsRolesApi';
import { useEntity, useMutation } from '@/hooks';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/label';
import { ArrowLeft, Edit, Trash2, Shield, CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';
import { LoadingState, ErrorState, CopyableId } from '@/components/common';
import type { Permission, Role } from '@/types/permissionsRoles';

export const PermissionDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [rolesUsingPermission, setRolesUsingPermission] = useState<Role[]>([]);
  const { hasPermission } = useAdminAuth();

  const canEdit = hasPermission('UPDATE_PERMISSION', 'permission');
  const canDelete = hasPermission('DELETE_PERMISSION', 'permission');

  // Memoize the fetch function to prevent useEntity from recreating on every render
  const fetchPermissionById = useCallback((permissionId: string) => permissionApi.getById(permissionId), []);

  // Memoize the error handler to prevent useEntity from recreating on every render
  const handleError = useCallback((error: unknown) => {
    // Only navigate away for 404 errors, not for other errors
    const apiError = error as { status?: number; message?: string };
    if (apiError.status === 404) {
      navigate('/admin/permissions');
    }
    // For other errors, let the ErrorState component handle it
  }, [navigate]);

  // Use entity hook for fetching permission
  const {
    data: permission,
    isLoading,
    error,
    refetch,
  } = useEntity<Permission>({
    fetchFn: fetchPermissionById,
    entityId: id,
    entityName: 'permission',
    enabled: !!id,
    onError: handleError,
  });

  const fetchRolesUsingPermissionRef = useRef(false);

  const fetchRolesUsingPermission = useCallback(async () => {
    if (!permission) return;
    try {
      const response = await roleApi.getByPermission(permission.name);
      setRolesUsingPermission(response.content || []);
    } catch (error: unknown) {
      const apiError = error as { message?: string; status?: number };
      console.error('Error fetching roles using permission:', apiError);
      // Don't set empty array on 429 - keep existing roles
      if (apiError.status !== 429) {
        setRolesUsingPermission([]);
      }
    }
  }, [permission]);

  // Fetch roles using this permission when permission changes
  useEffect(() => {
    if (!permission || fetchRolesUsingPermissionRef.current) {
      return;
    }
    
    fetchRolesUsingPermissionRef.current = true;
    fetchRolesUsingPermission().finally(() => {
      fetchRolesUsingPermissionRef.current = false;
    });
  }, [permission, fetchRolesUsingPermission]);

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error('Permission ID is required');
      await permissionApi.delete(id);
    },
    entityName: 'permission',
    operation: 'delete',
    onSuccess: () => {
      navigate('/admin/permissions');
    },
  });

  // Toggle status mutation
  const toggleStatusMutation = useMutation({
    mutationFn: async () => {
      if (!id || !permission) throw new Error('Permission ID is required');
      await permissionApi.toggleStatus(id, !permission.active);
    },
    entityName: 'permission',
    operation: 'update',
    successMessage: (_data, _variables) => `Permission ${permission && !permission.active ? 'activated' : 'deactivated'} successfully`,
    onSuccess: () => {
      refetch();
    },
  });

  const handleDelete = async () => {
    if (!id || !permission) return;
    if (!window.confirm(`Are you sure you want to delete permission "${permission.name}"?`)) return;
    await deleteMutation.mutate();
  };

  const handleToggleStatus = async () => {
    if (!id || !permission) return;
    await toggleStatusMutation.mutate();
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  if (isLoading) {
    return <LoadingState message="Loading permission..." />;
  }

  if (error || !permission) {
    return <ErrorState error={error || new Error('Permission not found')} onRetry={refetch} />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/admin/permissions">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h2 className="text-2xl font-bold tracking-tight flex items-center gap-2">
              <Shield className="h-5 w-5" />
              {permission.name}
            </h2>
            <p className="text-muted-foreground mt-1">Permission Details</p>
          </div>
        </div>
        <div className="flex gap-2">
          {canEdit && (
            <Button asChild>
              <Link to={`/admin/permissions/${permission.id}/edit`}>
                <Edit className="mr-2 h-4 w-4" />
                Edit
              </Link>
            </Button>
          )}
          {canEdit && (
            <Button variant="outline" onClick={handleToggleStatus} disabled={toggleStatusMutation.isLoading}>
              {permission.active ? (
                <>
                  <XCircle className="mr-2 h-4 w-4" />
                  Deactivate
                </>
              ) : (
                <>
                  <CheckCircle2 className="mr-2 h-4 w-4" />
                  Activate
                </>
              )}
            </Button>
          )}
          {canDelete && !permission.systemPermission && (
            <Button variant="destructive" onClick={handleDelete} disabled={deleteMutation.isLoading}>
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </Button>
          )}
        </div>
      </div>

      {/* System Permission Warning */}
      {permission.systemPermission && (
        <Card className="border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-yellow-800 dark:text-yellow-200">
              <AlertTriangle className="h-5 w-5" />
              <p className="text-sm font-medium">
                This is a system permission. Name cannot be changed and it cannot be deleted.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Status Badge */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Label>Status:</Label>
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  permission.active
                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                    : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
                }`}
              >
                {permission.active ? 'Active' : 'Inactive'}
              </span>
            </div>
            <div>
              {permission.systemPermission ? (
                <span className="px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                  System Permission
                </span>
              ) : (
                <span className="px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200">
                  Custom Permission
                </span>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="roles">Roles Using This Permission</TabsTrigger>
          <TabsTrigger value="audit">Audit</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Permission Information</CardTitle>
              <CardDescription>Basic permission details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <CopyableId id={permission.id || id || ''} label="Permission ID" />
              <div className="grid gap-6 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Name</Label>
                  <Input value={permission.name} disabled={permission.systemPermission} />
                </div>
                <div className="space-y-2">
                  <Label>Action</Label>
                  <Input value={permission.action || '—'} disabled />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Description</Label>
                <textarea
                  className="flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
                  value={permission.description || ''}
                  disabled
                />
              </div>

              <div className="space-y-2">
                <Label>Resource</Label>
                <Input value={permission.resource || '—'} disabled />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={permission.active}
                  disabled
                  className="rounded border-gray-300"
                />
                <Label>Active</Label>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Roles Using This Permission Tab */}
        <TabsContent value="roles" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Roles Using This Permission</CardTitle>
              <CardDescription>List of roles that include this permission</CardDescription>
            </CardHeader>
            <CardContent>
              {rolesUsingPermission.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No roles are using this permission
                </div>
              ) : (
                <div className="space-y-2">
                  {rolesUsingPermission.map((role) => (
                    <div
                      key={role.id}
                      className="flex items-center justify-between p-3 rounded-md border"
                    >
                      <div>
                        <div className="font-medium">{role.name}</div>
                        {role.description && (
                          <div className="text-sm text-muted-foreground">{role.description}</div>
                        )}
                      </div>
                      <Button variant="ghost" size="sm" asChild>
                        <Link to={`/admin/roles/${role.id}`}>View Role</Link>
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Audit Tab */}
        <TabsContent value="audit" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Audit Information</CardTitle>
              <CardDescription>Creation and modification history</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 text-sm">
                <div>
                  <span className="text-muted-foreground">Created: </span>
                  <span>{formatDate(permission.createdAt)}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Updated: </span>
                  <span>{formatDate(permission.updatedAt)}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Version: </span>
                  <span>{permission.version || 'N/A'}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Created By: </span>
                  <span>{permission.createdBy || 'N/A'}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Last Modified By: </span>
                  <span>{permission.lastModifiedBy || 'N/A'}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default PermissionDetailPage;
